import { NextPage } from 'next';
import { Container, Typography, Box, Stack, Divider } from '@mui/material';

import { useIntl } from '@klokhuis/core/Intl';
import { PageLayout } from '@klokhuis/core/PageLayout';
import { ROUTES } from '@klokhuis/core/Routing';

const Privacy: NextPage = () => {
  const { T } = useIntl();

  return (
    <PageLayout
      pageTitle={T('LBL_PRIVACY_TITLE')}
      metaDescription={T('LBL_PRIVACY_META')}
      route={ROUTES.PrivacyPolicy}
      showPropertyLinksGrid={false}
    >
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Last Updated: February 22, 2026
          </Typography>
        </Box>

        {/* Content Sections */}
        <Stack spacing={5}>
          {/* Section 1 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              1. Information We Collect
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              We collect personal information you provide such as name, email,
              phone number, date of birth, government-issued ID, profile
              photograph, business information, property listings, ownership
              documents, payment details, and messages. We also collect
              information automatically including IP address, browser type,
              device information, pages visited, search queries, and location
              data with your permission.
            </Typography>
          </Box>

          <Divider />

          {/* Section 2 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              2. Purpose of Data Collection
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              We collect your information to create and manage your account,
              facilitate property listings and searches, match you with relevant
              properties and users, process payments, provide customer support,
              improve platform experience, send marketing communications, detect
              fraud, and comply with legal obligations.
            </Typography>
          </Box>

          <Divider />

          {/* Section 3 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              We display your profile and listings to interested parties, send
              personalized recommendations, facilitate communication between
              users, and process transactions. We also analyze user behavior to
              identify market trends, test new features, and generate anonymous
              statistics to improve our platform.
            </Typography>
          </Box>

          <Divider />

          {/* Section 4 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              4. Data Sharing and Disclosure
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              When you express interest in a property, we share your name,
              email, and phone number with the property owner or advertiser. We
              may also share your data with service providers (payment
              processors, email services, analytics), cloud hosting providers,
              legal authorities when required by law, and other parties with
              your explicit consent.
            </Typography>
          </Box>

          <Divider />

          {/* Section 5 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              5. Your Rights Under Data Protection Laws
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              You have the right to access information we hold about you,
              correct or update your personal information, request deletion of
              your account and data, obtain your data in a structured format,
              withdraw your consent to communications, and file complaints with
              our Grievance Officer regarding data processing.
            </Typography>
          </Box>

          <Divider />

          {/* Section 6 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              6. Data Security
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              We implement industry-standard security measures including SSL/TLS
              encryption for data transmission, encryption of sensitive data at
              rest, secure password hashing, regular security audits, limited
              access on need-to-know basis, and secure data centers with access
              controls. While we implement robust security, no system is
              completely secure, and you acknowledge this risk when using our
              Platform.
            </Typography>
          </Box>

          <Divider />

          {/* Section 7 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              7. Cookies and Tracking
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              We use cookies and tracking technologies to enhance your
              experience. Essential cookies are required for functionality,
              performance cookies analyze usage, functional cookies remember
              preferences, and advertising cookies deliver targeted
              advertisements. You can manage cookies through your browser
              settings, though disabling them may affect platform functionality.
            </Typography>
          </Box>

          <Divider />

          {/* Section 8 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              8. Data Retention
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              We retain your personal data as long as your account remains
              active, it is necessary to provide our services, required by law,
              or needed to resolve disputes. Inactive accounts not used for 3
              years may be deleted or anonymized. Some data may be retained in
              backup systems for limited periods.
            </Typography>
          </Box>

          <Divider />

          {/* Section 9 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              9. Marketing Communications
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              You consent to receive communications via phone, SMS, WhatsApp,
              and email. You can opt out of marketing communications by clicking
              "unsubscribe" in emails, replying "STOP" to SMS, adjusting
              settings in your account, or contacting privacy@findmyprops.com.
            </Typography>
          </Box>

          <Divider />

          {/* Section 10 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              10. Contact & Grievance
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              For privacy inquiries, contact{' '}
              <strong>privacy@findmyprops.com</strong>. For grievances and
              complaints, reach out to{' '}
              <strong>grievances@findmyprops.com</strong>. We will acknowledge
              your complaint within 24 hours and respond within 72 hours.
            </Typography>
          </Box>

          <Divider />

          {/* Compliance Section */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Compliance & Legal
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              This Privacy Policy is governed by the laws of India, including
              the Digital Personal Data Protection Act 2023, The Information
              Technology Act 2000, Real Estate (Regulation and Development) Act
              2016, and other applicable laws and regulations.
            </Typography>
          </Box>

          <Divider />

          {/* Final Acknowledgment */}
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="caption" color="textSecondary">
              By using FindMyProps.com, you acknowledge that you have read and
              agree to be bound by this Privacy Policy.
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              sx={{ mt: 2 }}
            >
              © 2026 FindMyProps.com. All Rights Reserved. | Version 1.0
            </Typography>
          </Box>
        </Stack>
      </Container>
    </PageLayout>
  );
};

export default Privacy;
